import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CloudArrowUp, DownloadSimple, ArrowClockwise, Trash, Database } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtSize(n) {
  if (!n) return "0 B";
  const k = 1024;
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(n) / Math.log(k));
  return `${(n / Math.pow(k, i)).toFixed(1)} ${units[i]}`;
}

export default function Backup() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [drop, setDrop] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/backup/list"); setItems(data); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); }, []);

  const createNow = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/backup/create");
      toast.success(`Backup dibuat: ${data.filename}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };

  const download = async (fname) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/backup/download/${encodeURIComponent(fname)}`, { credentials: "include" });
      if (!res.ok) throw new Error("Gagal unduh");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error(e.message); }
  };

  const del = async (fname) => {
    if (!window.confirm(`Hapus backup ${fname}?`)) return;
    try { await api.delete(`/backup/${encodeURIComponent(fname)}`); load(); toast.success("Dihapus"); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const restore = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Pilih file backup terlebih dahulu"); return; }
    if (!window.confirm(drop ? "PERINGATAN: seluruh data yang ada akan DIHAPUS dan diganti dengan isi file backup. Lanjutkan?" : "Restore backup ke database? Data lama akan di-merge (upsert).")) return;
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = `${BACKEND_URL}/api/backup/restore?drop_existing=${drop}`;
      const res = await fetch(url, { method: "POST", credentials: "include", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Gagal restore");
      const totals = Object.values(data.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`Restore selesai: ${totals} dokumen di-restore`);
      setFile(null); load();
    } catch (e) { toast.error(e.message); }
    finally { setRestoring(false); }
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">Keamanan</div>
        <h1 className="font-display text-3xl font-bold text-[#064E3B]">Backup & Restore Database</h1>
        <p className="text-sm text-slate-500 mt-1">
          Backup otomatis harian aktif. Berkas disimpan di server dan tetap 30 terbaru.
        </p>
      </div>

      <div className="imsyaf-card p-5 flex flex-wrap items-center gap-3">
        <Database size={22} weight="duotone" className="text-[#166534]" />
        <div className="flex-1 min-w-[200px]">
          <div className="font-semibold text-[#064E3B]">Buat Backup Sekarang</div>
          <div className="text-xs text-slate-500">Menyimpan seluruh koleksi ke berkas JSON lokal.</div>
        </div>
        <button onClick={createNow} disabled={loading} className="imsyaf-btn-primary" data-testid="backup-create-btn">
          <ArrowClockwise size={16}/> {loading ? "Menyimpan…" : "Buat Backup"}
        </button>
      </div>

      <div className="imsyaf-card overflow-hidden">
        <table className="w-full text-sm zebra">
          <thead className="bg-slate-50"><tr>
            <th className="text-left px-4 py-2">File</th>
            <th className="text-left px-4 py-2">Waktu</th>
            <th className="text-left px-4 py-2">Ukuran</th>
            <th className="text-right px-4 py-2">Aksi</th>
          </tr></thead>
          <tbody>{items.map((b) => (
            <tr key={b.filename} className="border-t border-slate-100">
              <td className="px-4 py-2 font-mono">{b.filename}</td>
              <td className="px-4 py-2 text-xs text-slate-500">{b.created_at?.replace("T", " ").slice(0, 19)}</td>
              <td className="px-4 py-2">{fmtSize(b.size_bytes)}</td>
              <td className="px-4 py-2 text-right space-x-1">
                <button className="imsyaf-btn-ghost" onClick={() => download(b.filename)} title="Unduh" data-testid={`backup-download-${b.filename}`}><DownloadSimple size={14}/></button>
                <button className="imsyaf-btn-ghost text-red-600" onClick={() => del(b.filename)} title="Hapus"><Trash size={14}/></button>
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-slate-400">Belum ada backup.</td></tr>}
          </tbody>
        </table>
      </div>

      <form onSubmit={restore} className="imsyaf-card p-5 space-y-3">
        <div className="flex items-center gap-3">
          <CloudArrowUp size={22} weight="duotone" className="text-[#166534]" />
          <div>
            <div className="font-semibold text-[#064E3B]">Restore dari Berkas</div>
            <div className="text-xs text-slate-500">Unggah file JSON hasil backup untuk memulihkan data.</div>
          </div>
        </div>
        <input type="file" accept="application/json,.json" onChange={(e) => setFile(e.target.files[0])} className="imsyaf-input" data-testid="backup-file" />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={drop} onChange={(e) => setDrop(e.target.checked)} data-testid="backup-drop" />
          <span>Hapus data lama sebelum restore (drop_existing) — <b className="text-red-600">berbahaya</b></span>
        </label>
        <button className="imsyaf-btn-primary" disabled={restoring} data-testid="backup-restore-btn">
          <CloudArrowUp size={16}/> {restoring ? "Restoring…" : "Restore Sekarang"}
        </button>
      </form>
    </div>
  );
}
